import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Loader2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Filter,
  MapPin,
  User,
  Package,
  AlertTriangle,
  Clock,
} from "lucide-react";

const MOTIVOS = [
  { value: "", label: "Todos los pendientes" },
  { value: "datos_faltantes", label: "Datos faltantes" },
  { value: "suscripcion_invalida", label: "Suscripción inválida" },
  { value: "cobertura", label: "Cobertura (fuera/lleno)" },
  { value: "sin_descripcion", label: "Sin descripción" },
  { value: "escalado", label: "Escalado a supervisor" },
];

const STATUS_STYLES = {
  approved: { bg: "bg-emerald-50", fg: "text-emerald-800", label: "Aprobado" },
  bypassed: { bg: "bg-slate-100", fg: "text-slate-700", label: "Bypass" },
  pending_data: { bg: "bg-amber-50", fg: "text-amber-800", label: "Datos incompletos" },
  pending_description: { bg: "bg-amber-50", fg: "text-amber-800", label: "Sin descripción" },
  pending_agendador: { bg: "bg-orange-50", fg: "text-orange-800", label: "Con agendador" },
  no_coverage: { bg: "bg-red-50", fg: "text-red-800", label: "Fuera de cobertura" },
  coverage_full_waiting_client: { bg: "bg-yellow-50", fg: "text-yellow-800", label: "Zona llena · esperando cliente" },
  escalated_supervisor: { bg: "bg-purple-50", fg: "text-purple-800", label: "Escalado a supervisor" },
  not_evaluated: { bg: "bg-gray-100", fg: "text-gray-600", label: "Sin evaluar" },
};

export default function AdminA1Audit() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], total: 0, count: 0 });
  const [motivo, setMotivo] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [reEvaluating, setReEvaluating] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const limit = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      if (motivo) params.motivo = motivo;
      if (q.trim()) params.q = q.trim();
      const { data: res } = await api.get("/a1/audit", { params });
      setData(res);
    } catch (e) {
      toast.error("Error cargando auditoría: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const { data: res } = await api.get("/a1/dashboard");
      setDashboard(res);
    } catch (_) {
      // silencioso
    }
  };

  useEffect(() => {
    load();
    loadDashboard();
  }, [motivo, page]);

  const reEvaluate = async (id) => {
    setReEvaluating(id);
    try {
      const { data: res } = await api.post(`/a1/evaluate/${id}`);
      toast.success(`OT ${id} re-evaluada → ${res.status}`);
      await load();
      await loadDashboard();
    } catch (e) {
      toast.error("Error re-evaluando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setReEvaluating(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const kpisByStatus = useMemo(() => {
    if (!dashboard?.by_status) return [];
    return dashboard.by_status.filter((s) =>
      ["pending_data", "pending_description", "no_coverage", "coverage_full_waiting_client", "escalated_supervisor"].includes(s.status)
    );
  }, [dashboard]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-a1-audit-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
              style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>
                Agente A1 · Auditoría
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>
              OTs pendientes de prefactibilidad
            </h1>
            <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
              Filtra por motivo del rechazo y re-evalúa cada OT tras corregir datos en Odoo.
            </p>
          </div>
          <button onClick={() => { load(); loadDashboard(); }} className="neu-btn neu-btn-primary" data-testid="a1-audit-refresh">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refrescar
          </button>
        </div>

        {/* KPIs */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {kpisByStatus.map((s) => {
              const st = STATUS_STYLES[s.status] || {};
              return (
                <button
                  key={s.status}
                  onClick={() => { setPage(0); setMotivo(s.status === "no_coverage" || s.status === "coverage_full_waiting_client" ? "cobertura" : s.status === "pending_description" ? "sin_descripcion" : s.status === "escalated_supervisor" ? "escalado" : "datos_faltantes"); }}
                  className={`rounded-xl px-3 py-2 text-left transition-all hover:-translate-y-0.5 ${st.bg}`}
                  style={{ boxShadow: "-3px -3px 8px rgba(255,255,255,0.75), 3px 3px 8px rgba(163,177,198,0.5)" }}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${st.fg} opacity-80`}>{st.label || s.status}</div>
                  <div className={`text-2xl font-bold tabular-nums ${st.fg}`}>{s.count.toLocaleString("es-EC")}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
            <select
              value={motivo}
              onChange={(e) => { setPage(0); setMotivo(e.target.value); }}
              className="neu-input pl-9 pr-4"
              data-testid="a1-audit-motivo-filter"
            >
              {MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="relative flex-1 max-w-md min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setPage(0), load())}
              placeholder="OT, cliente, código de suscripción..."
              className="neu-input pl-9"
              data-testid="a1-audit-search"
            />
          </div>
          <button onClick={() => { setPage(0); load(); }} className="neu-btn">
            Buscar
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            {data.total.toLocaleString("es-EC")} totales · página {page + 1}/{totalPages}
          </span>
        </div>

        {/* Tabla */}
        <div className="neu-card overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : data.rows.length === 0 ? (
            <div className="py-16 text-center text-gray-500">Sin OTs que coincidan con el filtro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm neu-table">
                <thead>
                  <tr>
                    <th className="px-3 py-3 text-left">OT</th>
                    <th className="px-3 py-3 text-left">Cliente</th>
                    <th className="px-3 py-3 text-left">Suscripción</th>
                    <th className="px-3 py-3 text-left">Estado A1</th>
                    <th className="px-3 py-3 text-left">Motivo</th>
                    <th className="px-3 py-3 text-left">Evaluada</th>
                    <th className="px-3 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const reasons = Array.isArray(r.a1_reasons_json) ? r.a1_reasons_json : [];
                    const st = STATUS_STYLES[r.a1_status] || { bg: "bg-gray-100", fg: "text-gray-700", label: r.a1_status };
                    return (
                      <tr key={r.odoo_task_id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`a1-audit-row-${r.odoo_task_id}`}>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="font-semibold tabular-nums" style={{ color: "#0A3747" }}>{r.odoo_task_id}</div>
                          <div className="text-[11px] text-gray-500">{r.task_number || "—"}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5 font-medium" style={{ color: "#0A3747" }}>
                            <User size={11} className="text-gray-400 flex-shrink-0" />
                            {r.client_name || <span className="text-gray-400 italic">sin nombre</span>}
                          </div>
                          <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
                            {r.client_vat || "—"}
                            {r.client_lat != null && r.client_lon != null && (
                              <span className="text-emerald-600 inline-flex items-center gap-0.5"><MapPin size={9} /> gps</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-mono text-xs" style={{ color: "#0A3747" }}>{r.subscription_code || "—"}</div>
                          <div className="text-[11px] text-gray-500">{r.plan_type || "—"}</div>
                          {/* v3.8.1 · badge subscription_stage_name eliminado: enfoque 100% en OT */}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.fg}`}>{st.label}</span>
                        </td>
                        <td className="px-3 py-3 max-w-xs">
                          <div className="text-[11px] text-gray-700 line-clamp-2" title={reasons.join(" · ")}>
                            {reasons[0] || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-[11px] text-gray-500 whitespace-nowrap">
                          {r.a1_evaluated_at ? new Date(r.a1_evaluated_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                          {r.a1_pending_since && (
                            <div className="text-amber-600 flex items-center gap-0.5 mt-0.5">
                              <Clock size={9} />
                              {Math.floor((Date.now() - new Date(r.a1_pending_since).getTime()) / 3600000)}h pendiente
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            onClick={() => reEvaluate(r.odoo_task_id)}
                            disabled={reEvaluating === r.odoo_task_id}
                            className="neu-btn text-xs"
                            data-testid={`a1-reeval-${r.odoo_task_id}`}
                            title="Re-evaluar A1 (dispara POST /api/a1/evaluate/{id})"
                          >
                            {reEvaluating === r.odoo_task_id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RotateCcw size={12} />
                            )}
                            Re-evaluar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        {data.total > limit && (
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="neu-btn"
              data-testid="a1-audit-page-prev"
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <span className="px-4 py-2 text-sm text-gray-600 tabular-nums">
              Página {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="neu-btn"
              data-testid="a1-audit-page-next"
            >
              Siguiente <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
